/**
 * Integrytas Group - Base App (MOBILE)
 * Page: APR.DELEGATIONS — mobile-native.
 *
 * Touch-first counterpart of src/pages/admin/ApprovalDelegationsPage.tsx. A
 * DISTINCT presentation (dense card list + facet chips + bottom-sheet detail +
 * bottom-sheet create form) over the SAME data/logic layer:
 *   - reads via apiDb (apr_delegation, plus csm_user_master to resolve the
 *     delegator/delegate FKs to names — §17.B4, never a raw id),
 *   - writes go through the delegation engine's own sanctioned RPCs
 *     apr_set_delegation / apr_cancel_delegation exactly as the desktop page does
 *     (these encapsulate the engine's validation; they are NOT direct table
 *     insert/update/delete).
 * It never re-hosts the desktop grid; §16.
 */

/* rbac-affordance-ok: self-service, same as the desktop page — the delegator
   is csm_current_user_id(), and cancelling is owner-or-admin (APR.NOT_OWNER). */
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Search, Inbox, Plus, X, UserCheck, Ban } from 'lucide-react';
import { apiDb } from '../../lib/api_client';
import { useFormatDateTime } from '../../lib/format_date';
import { MobilePageHeader, MobileStatStrip, MobileContentCard, MobileCardRow, MobileDetailRow, MobileFilterChips, MobileStatusPill } from '../components';
import { MobileRelatedChildren } from '../components/MobileRelatedChildren';
import type { MobileLeadTone, MobilePillTone } from '../components';
import type { MobilePageComponentProps } from '../mobile_page_components';

interface MobileDelegationRow {
  apr_delegation_id: number;
  delegator_user_id: number;
  delegate_user_id: number;
  valid_from: string;
  valid_to: string | null;
  scope_entity: string | null;
  is_active: 'Y' | 'N';
  delegate_name: string;
  delegator_name: string;
  is_mine: boolean;
}

interface UserRow { user_id: number; display_name: string; email: string }

interface RpcResult { ok: boolean; error_code?: string }

type StatusFacet = 'all' | 'active' | 'inactive';

// Active/inactive → pill + lead tone enums (Direction C).
const pillTone = (isActive: 'Y' | 'N'): MobilePillTone => (isActive === 'Y' ? 'ok' : 'no');
const leadTone = (isActive: 'Y' | 'N'): MobileLeadTone => (isActive === 'Y' ? 'green' : 'slate');

export function MobileApprovalDelegationsPage({ session }: MobilePageComponentProps) {
  const { t } = useTranslation();
  const formatDateTime = useFormatDateTime();
  const myId = session.effective_user.user_id;

  const [rows, setRows] = useState<MobileDelegationRow[]>([]);
  const [users, setUsers] = useState<UserRow[]>([]);
  const [filter, setFilter] = useState('');
  const [statusFacet, setStatusFacet] = useState<StatusFacet>('active');
  const [selected, setSelected] = useState<MobileDelegationRow | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [actionBusy, setActionBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Create-form state.
  const [delegate, setDelegate] = useState('');
  const [validFrom, setValidFrom] = useState('');
  const [validTo, setValidTo] = useState('');
  const [scopeEntity, setScopeEntity] = useState('');

  const reload = async () => {
    setBusy(true);
    setError(null);
    try {
      // Resolve the delegator/delegate FKs to names — never render a raw id (§17.B4).
      const { data: rawUsers, error: uErr } = await apiDb
        .from('csm_user_master')
        .select('user_id, display_name, email')
        .eq('is_active', 'Y')
        .order('display_name');
      if (uErr) throw uErr;
      const userRows = (rawUsers ?? []) as UserRow[];
      setUsers(userRows);
      const nameOf = (uid: number): string =>
        userRows.find((u) => u.user_id === uid)?.display_name ?? t('mobile.deleg.user_fallback', 'User {{id}}', { id: uid });

      const { data: rawRows, error: dErr } = await apiDb
        .from('apr_delegation')
        .select('apr_delegation_id, delegator_user_id, delegate_user_id, valid_from, valid_to, scope_entity, is_active')
        .order('apr_delegation_id', { ascending: false });
      if (dErr) throw dErr;

      type RawRow = Omit<MobileDelegationRow, 'delegate_name' | 'delegator_name' | 'is_mine'>;
      const mapped: MobileDelegationRow[] = ((rawRows ?? []) as RawRow[]).map((r) => ({
        apr_delegation_id: r.apr_delegation_id,
        delegator_user_id: r.delegator_user_id,
        delegate_user_id: r.delegate_user_id,
        valid_from: r.valid_from,
        valid_to: r.valid_to,
        scope_entity: r.scope_entity,
        is_active: r.is_active,
        delegate_name: nameOf(r.delegate_user_id),
        delegator_name: nameOf(r.delegator_user_id),
        is_mine: r.delegator_user_id === myId,
      }));
      setRows(mapped);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { void reload(); }, []);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return rows.filter((r) => {
      if (statusFacet === 'active' && r.is_active !== 'Y') return false;
      if (statusFacet === 'inactive' && r.is_active !== 'N') return false;
      if (!q) return true;
      return (
        r.delegate_name.toLowerCase().includes(q) ||
        r.delegator_name.toLowerCase().includes(q) ||
        (r.scope_entity ?? '').toLowerCase().includes(q)
      );
    });
  }, [rows, filter, statusFacet]);

  const facets: { key: StatusFacet; label: string }[] = [
    { key: 'all', label: t('mobile.deleg.all', 'All') },
    { key: 'active', label: t('common.boolean.active', 'Active') },
    { key: 'inactive', label: t('common.boolean.inactive', 'Inactive') },
  ];

  // Summary counts over the full (unfiltered) set — presentation-only derivation.
  const stats = useMemo(() => ({
    total: rows.length,
    active: rows.filter((r) => r.is_active === 'Y').length,
    inactive: rows.filter((r) => r.is_active === 'N').length,
  }), [rows]);

  const scopeText = (r: MobileDelegationRow): string =>
    r.scope_entity ?? t('mobile.deleg.all_entities', 'All entities');

  const subText = (r: MobileDelegationRow): string =>
    r.is_mine
      ? t('mobile.deleg.to', 'To {{name}}', { name: r.delegate_name })
      : t('mobile.deleg.from', 'From {{name}}', { name: r.delegator_name });

  const resetCreate = () => { setDelegate(''); setValidFrom(''); setValidTo(''); setScopeEntity(''); };

  const submitCreate = async () => {
    if (!delegate) { setError(t('mobile.deleg.err_choose', 'Choose a delegate.')); return; }
    setActionBusy(true);
    setError(null);
    try {
      const { data, error: e } = await apiDb.rpc('apr_set_delegation', {
        p_delegate_user_id: Number(delegate),
        p_valid_from: validFrom || null,
        p_valid_to: validTo || null,
        p_scope_entity: scopeEntity.trim() || null,
      });
      if (e) throw e;
      const res = data as RpcResult | null;
      if (!res?.ok) throw new Error(res?.error_code ?? 'APR.FAILED');
      setCreateOpen(false);
      resetCreate();
      await reload();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setActionBusy(false);
    }
  };

  const cancelDelegation = async (r: MobileDelegationRow) => {
    setActionBusy(true);
    setError(null);
    try {
      const { data, error: e } = await apiDb.rpc('apr_cancel_delegation', { p_delegation_id: r.apr_delegation_id });
      if (e) throw e;
      const res = data as RpcResult | null;
      if (!res?.ok) throw new Error(res?.error_code ?? 'APR.FAILED');
      setSelected(null);
      await reload();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setActionBusy(false);
    }
  };

  const delegateOptions = users.filter((u) => u.user_id !== myId);

  return (
    <section className="m-screen">
      <MobilePageHeader
        title={t('mobile.deleg.title', 'Approval Delegations')}
        description={t('mobile.deleg.description', 'Delegate your approval authority to another user for a period.')}
      />

      <MobileStatStrip
        stats={[
          { value: stats.total, label: t('mobile.deleg.kpi_total', 'Total') },
          { value: stats.active, label: t('common.boolean.active', 'Active') },
          { value: stats.inactive, label: t('common.boolean.inactive', 'Inactive') },
        ]}
      />

      <div className="m-searchbox">
        <Search />
        <input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder={t('mobile.deleg.search', 'Search delegations')}
          aria-label={t('mobile.deleg.search', 'Search delegations')}
        />
      </div>

      <MobileFilterChips
        chips={facets.map((f) => ({ value: f.key, label: f.label }))}
        isActive={(v) => statusFacet === v}
        onSelect={(v) => setStatusFacet(v as StatusFacet)}
      />

      <button
        type="button"
        className="m-btn m-btn-primary"
        style={{ margin: '6px 0 4px' }}
        onClick={() => { setError(null); resetCreate(); setCreateOpen(true); }}
      >
        <Plus size={16} /> {t('mobile.deleg.new', 'New delegation')}
      </button>

      {error && !createOpen && !selected && (
        <MobileContentCard>
          <div className="m-empty" style={{ color: 'var(--color-danger)' }}>{error}</div>
        </MobileContentCard>
      )}

      {filtered.length === 0 && !busy ? (
        <MobileContentCard><div className="m-empty"><Inbox /> {t('mobile.deleg.none', 'No delegations match')}</div></MobileContentCard>
      ) : (
        <MobileContentCard list>
          {filtered.map((r) => (
            <MobileCardRow
              key={r.apr_delegation_id}
              lead={<UserCheck />}
              leadTone={leadTone(r.is_active)}
              title={r.delegate_name}
              subtitle={`${subText(r)} · ${scopeText(r)}`}
              trailing={
                <MobileStatusPill tone={pillTone(r.is_active)}>
                  {r.is_active === 'Y' ? t('common.boolean.active', 'Active') : t('common.boolean.inactive', 'Inactive')}
                </MobileStatusPill>
              }
              chevron
              onClick={() => { setError(null); setSelected(r); }}
            />
          ))}
        </MobileContentCard>
      )}

      {/* Detail bottom-sheet */}
      <div className={'m-scrim' + (selected ? ' m-open' : '')} onClick={() => { if (!actionBusy) setSelected(null); }} />
      <div className={'m-sheet' + (selected ? ' m-open' : '')} aria-label={t('mobile.deleg.detail', 'Delegation detail')}>
        <div className="m-grabber" />
        {selected && (
          <>
            <div className="m-sheet-id">
              <span className="m-av"><UserCheck size={20} /></span>
              <span>
                <div className="m-nm">{selected.delegate_name}</div>
                <div className="m-em">{subText(selected)}</div>
              </span>
            </div>
            <MobileContentCard>
              <MobileDetailRow
                label={t('mobile.deleg.status', 'Status')}
                value={
                  <MobileStatusPill tone={pillTone(selected.is_active)}>
                    {selected.is_active === 'Y' ? t('common.boolean.active', 'Active') : t('common.boolean.inactive', 'Inactive')}
                  </MobileStatusPill>
                }
              />
              <MobileDetailRow label={t('mobile.deleg.delegator', 'Delegator')} value={selected.delegator_name} />
              <MobileDetailRow label={t('mobile.deleg.delegate', 'Delegate')} value={selected.delegate_name} />
              <MobileDetailRow label={t('mobile.deleg.valid_from', 'From')} value={formatDateTime(selected.valid_from)} />
              <MobileDetailRow label={t('mobile.deleg.valid_to', 'To')} value={selected.valid_to ? formatDateTime(selected.valid_to) : t('mobile.deleg.open_ended', 'Open-ended')} />
              <MobileDetailRow label={t('mobile.deleg.scope', 'Scope')} value={scopeText(selected)} />
            </MobileContentCard>
            {/* §17.B13 — the row's related records, stacked one group per block. */}
            <MobileContentCard>
              <MobileRelatedChildren
                session={session}
                groups={[
                  { key: 'delegate', table: 'csm_user_master', pkColumn: 'user_id',
                    title: t('mobile.deleg.related.delegate', 'Delegate'),
                    items: [{ id: selected.delegate_user_id, label: selected.delegate_name }] },
                  { key: 'delegator', table: 'csm_user_master', pkColumn: 'user_id',
                    title: t('mobile.deleg.related.delegator', 'Delegator'),
                    items: [{ id: selected.delegator_user_id, label: selected.delegator_name }] },
                ]}
              />
            </MobileContentCard>

            {error && <MobileContentCard><div className="m-empty" style={{ color: 'var(--color-danger)' }}>{error}</div></MobileContentCard>}

            <div className="m-actionbar">
              <button
                type="button"
                className="m-btn m-btn-danger"
                disabled={actionBusy || selected.is_active !== 'Y' || !selected.is_mine}
                title={!selected.is_mine ? t('mobile.deleg.not_owner', 'Only the delegator can cancel this') : undefined}
                onClick={() => void cancelDelegation(selected)}
              >
                <Ban size={16} />
                {t('mobile.deleg.cancel', 'Cancel')}
              </button>
              <button type="button" className="m-btn m-btn-ghost" disabled={actionBusy} onClick={() => setSelected(null)} aria-label={t('mobile.close', 'Close')}>
                <X size={16} />
              </button>
            </div>
          </>
        )}
      </div>

      {/* Create bottom-sheet */}
      <div className={'m-scrim' + (createOpen ? ' m-open' : '')} onClick={() => { if (!actionBusy) setCreateOpen(false); }} />
      <div className={'m-sheet' + (createOpen ? ' m-open' : '')} aria-label={t('mobile.deleg.new', 'New delegation')}>
        <div className="m-grabber" />
        {createOpen && (
          <>
            <div className="m-sheet-id">
              <span className="m-av"><Plus size={20} /></span>
              <span>
                <div className="m-nm">{t('mobile.deleg.new', 'New delegation')}</div>
                <div className="m-em">{t('mobile.deleg.new_sub', 'Delegate my approvals')}</div>
              </span>
            </div>

            <div className="m-fieldcard">
              <div className="m-formrow">
                <label htmlFor="m-deleg-user">{t('mobile.deleg.delegate', 'Delegate')}</label>
                <select id="m-deleg-user" className="m-sel" value={delegate} onChange={(e) => setDelegate(e.target.value)}>
                  <option value="">{t('mobile.deleg.choose', '— choose —')}</option>
                  {delegateOptions.map((u) => (
                    <option key={u.user_id} value={String(u.user_id)}>{u.display_name}</option>
                  ))}
                </select>
              </div>
              <div className="m-formrow">
                <label htmlFor="m-deleg-from">{t('mobile.deleg.valid_from', 'From')}</label>
                <input id="m-deleg-from" type="datetime-local" className="m-inp" value={validFrom} onChange={(e) => setValidFrom(e.target.value)} />
              </div>
              <div className="m-formrow">
                <label htmlFor="m-deleg-to">{t('mobile.deleg.valid_to_opt', 'To (optional)')}</label>
                <input id="m-deleg-to" type="datetime-local" className="m-inp" value={validTo} onChange={(e) => setValidTo(e.target.value)} />
              </div>
              <div className="m-formrow">
                <label htmlFor="m-deleg-scope">{t('mobile.deleg.scope_opt', 'Scope entity (optional)')}</label>
                <input
                  id="m-deleg-scope"
                  className="m-inp"
                  value={scopeEntity}
                  onChange={(e) => setScopeEntity(e.target.value)}
                  placeholder={t('mobile.deleg.all_entities', 'All entities')}
                />
              </div>
            </div>

            {error && <MobileContentCard><div className="m-empty" style={{ color: 'var(--color-danger)' }}>{error}</div></MobileContentCard>}

            <div className="m-actionbar">
              <button type="button" className="m-btn m-btn-primary" disabled={actionBusy} onClick={() => void submitCreate()}>
                {actionBusy ? t('common.saving', 'Saving…') : t('mobile.deleg.create', 'Create')}
              </button>
              <button type="button" className="m-btn m-btn-ghost" disabled={actionBusy} onClick={() => setCreateOpen(false)} aria-label={t('common.cancel', 'Cancel')}>
                <X size={16} />
              </button>
            </div>
          </>
        )}
      </div>
    </section>
  );
}
